import { useEffect, useState } from 'react'
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore'
import type { Timestamp } from 'firebase/firestore'
import { db } from '../lib/firebase'

export interface MonthResult {
  year: number
  month: number
  found: number
  created: number
  updated: number
  unchanged: number
  failed?: boolean
}

export interface ScrapeRun {
  id: string
  vendor: string
  startedAt: Timestamp
  finishedAt: Timestamp
  status: 'ok' | 'failed'
  months: MonthResult[]
  found: number
  created: number
  updated: number
  unchanged: number
  errors?: string[]
}

/**
 * Scrape history exists because the failure mode of a scraper is silence. TSL
 * publishes no contract and can restructure their markup at any time, and a
 * parser that returns nothing looks exactly like a quiet month — so runs record
 * what they found, and the parser cross-checks against the page's own count.
 */
export function useScrapeRuns(vendor: string, max = 20) {
  const [runs, setRuns] = useState<ScrapeRun[] | null>(null)

  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'scrapeRuns'), orderBy('startedAt', 'desc'), limit(max)),
      (snap) =>
        setRuns(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as Omit<ScrapeRun, 'id'>) }))
            .filter((r) => r.vendor === vendor),
        ),
      () => setRuns([]),
    )
  }, [vendor, max])

  return runs
}
